<h2 mat-dialog-title class="tw-m-0 tw-text-lg tw-font-semibold">Cleanup Result</h2>
<div
  appGlossScroll
  mat-dialog-content
  class="tw-max-h-[75vh] tw-overflow-y-auto tw-min-w-[min(95vw,64rem)]"
>
  <p class="tw-text-sm tw-text-dt-text-secondary tw-m-0 tw-mb-4">
    {{ data.groups.length }} duplicate group(s) cleaned up.
    @if ((data.deletedSourceTransactions?.length ?? 0) > 0) {
      <span class="tw-ml-2 tw-text-orange-600">
        + {{ data.deletedSourceTransactions!.length }} orphaned source transaction(s) removed.
      </span>
    }
    @if (data.deletedSourceTransactionCount) {
      <span class="tw-ml-2 tw-text-orange-600">
        + {{ data.deletedSourceTransactionCount }} orphaned source transaction(s) removed.
      </span>
    }
  </p>

  @if (hasAccountCascade) {
    <h3 class="tw-text-base tw-font-semibold tw-text-dt-text-headings tw-m-0 tw-mb-3">Accounts</h3>
  }

  <!-- ── Accounts / generic duplicates: Before / After ── -->
  <div class="tw-flex tw-gap-4 tw-w-full">
    <!-- Left: Before cleanup -->
    <div class="tw-flex-1 tw-min-w-0">
      <h3 class="tw-text-sm tw-font-semibold tw-text-dt-text-secondary tw-m-0 tw-mb-2">
        Before Clean Up
      </h3>
      <div
        appGlossScroll
        class="tw-flex tw-flex-col tw-gap-3 tw-border tw-border-dt-border-secondary tw-rounded-lg tw-p-3 tw-bg-dt-surface-elevated tw-max-h-[60vh] tw-overflow-y-auto"
      >
        @for (group of data.groups; track $index) {
          <div
            class="tw-border tw-border-dt-border-secondary tw-rounded tw-bg-dt-surface-elevated tw-p-2"
          >
            <div class="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mb-1">
              <span class="tw-text-xs tw-font-mono tw-text-dt-text-secondary tw-truncate">
                id: {{ group.kept.id }}
                <span class="tw-text-dt-text-disabled tw-ml-1"
                  >({{ group.deleted.length + 1 }} copies)</span
                >
              </span>
              <button
                type="button"
                mat-stroked-button
                class="tw-text-xs tw-shrink-0"
                (click)="toggleBeforeExpand($index)"
              >
                {{ isBeforeExpanded($index) ? "Collapse" : "Show all" }}
              </button>
            </div>

            @if (!isBeforeExpanded($index)) {
              <div
                class="tw-text-xs tw-font-mono tw-p-2 tw-bg-dt-surface-sunken tw-rounded tw-text-dt-text-secondary"
              >
                <span class="tw-text-dt-text-tertiary">Representative:</span>
                {{ group.kept.id?.slice(0, 20) }}…
              </div>
            }

            @if (isBeforeExpanded($index)) {
              @if (group.allDeleted) {
                <!-- All copies deleted (no valid link) -->
                <div
                  class="tw-mb-2 tw-p-2 tw-rounded tw-border tw-border-color-status-error-border tw-bg-color-status-error-bg"
                >
                  <div class="tw-flex tw-items-center tw-gap-2 tw-mb-1">
                    <span
                      class="tw-text-xs tw-font-semibold tw-text-color-status-error-text tw-uppercase tw-tracking-wide"
                      >Deleted (no valid link)</span
                    >
                    <span
                      class="tw-text-xs tw-font-mono tw-bg-color-status-error-bg tw-text-color-status-error-text tw-px-1.5 tw-py-0.5 tw-rounded"
                    >
                      dc: {{ formatDc(group.kept) }}
                    </span>
                  </div>
                  <pre
                    appGlossScroll
                    class="tw-m-0 tw-text-xs tw-font-mono tw-max-h-32 tw-overflow-auto tw-p-2 tw-bg-neutral-900 tw-text-dt-text-disabled tw-rounded tw-whitespace-pre-wrap tw-break-words tw-select-all gs-dark"
                    >{{ itemJson(group.kept) }}</pre
                  >
                </div>
              } @else {
                <!-- Kept item highlighted -->
                <div
                  class="tw-mb-2 tw-p-2 tw-rounded tw-border-2 tw-border-color-status-success-border tw-bg-color-status-success-bg"
                >
                  <div class="tw-flex tw-items-center tw-gap-2 tw-mb-1">
                    <span
                      class="tw-text-xs tw-font-semibold tw-text-color-status-success-text tw-uppercase tw-tracking-wide"
                      >Kept</span
                    >
                    <span
                      class="tw-text-xs tw-font-mono tw-bg-color-status-success-bg tw-text-color-status-success-text tw-px-1.5 tw-py-0.5 tw-rounded"
                    >
                      dc: {{ formatDc(group.kept) }}
                    </span>
                  </div>
                  <pre
                    appGlossScroll
                    class="tw-m-0 tw-text-xs tw-font-mono tw-max-h-32 tw-overflow-auto tw-p-2 tw-bg-neutral-900 tw-text-dt-text-disabled tw-rounded tw-whitespace-pre-wrap tw-break-words tw-select-all gs-dark"
                    >{{ itemJson(group.kept) }}</pre
                  >
                </div>
              }
              <!-- Deleted items -->
              @for (del of group.deleted; track $index) {
                <div
                  class="tw-mb-2 tw-p-2 tw-rounded tw-border tw-border-color-status-error-border tw-bg-color-status-error-bg"
                >
                  <div class="tw-flex tw-items-center tw-gap-2 tw-mb-1">
                    <span
                      class="tw-text-xs tw-font-semibold tw-text-color-status-error-text tw-uppercase tw-tracking-wide"
                      >Deleted</span
                    >
                    <span
                      class="tw-text-xs tw-font-mono tw-bg-color-status-error-bg tw-text-color-status-error-text tw-px-1.5 tw-py-0.5 tw-rounded"
                    >
                      dc: {{ formatDc(del) }}
                    </span>
                  </div>
                  <pre
                    appGlossScroll
                    class="tw-m-0 tw-text-xs tw-font-mono tw-max-h-32 tw-overflow-auto tw-p-2 tw-bg-neutral-900 tw-text-dt-text-disabled tw-rounded tw-whitespace-pre-wrap tw-break-words tw-select-all gs-dark"
                    >{{ itemJson(del) }}</pre
                  >
                </div>
              }
            }
          </div>
        }
      </div>
    </div>

    <!-- Right: After cleanup (kept items) -->
    <div class="tw-flex-1 tw-min-w-0">
      <h3 class="tw-text-sm tw-font-semibold tw-text-dt-text-secondary tw-m-0 tw-mb-2">
        After Clean Up (Kept)
      </h3>
      <div
        appGlossScroll
        class="tw-flex tw-flex-col tw-gap-3 tw-border tw-border-dt-border-secondary tw-rounded-lg tw-p-3 tw-bg-dt-surface-elevated tw-max-h-[60vh] tw-overflow-y-auto"
      >
        @for (group of data.groups; track $index) {
          @if (group.allDeleted) {
            <div
              class="tw-border tw-border-color-status-error-border tw-rounded tw-bg-color-status-error-bg tw-p-2"
            >
              <span
                class="tw-text-xs tw-font-semibold tw-text-color-status-error-text tw-uppercase"
              >
                All copies deleted — no valid link
              </span>
            </div>
          } @else {
            <div
              class="tw-border-2 tw-border-color-status-success-border tw-rounded tw-bg-color-status-success-bg tw-p-2"
            >
              <div class="tw-flex tw-items-center tw-gap-2 tw-mb-1">
                <span class="tw-text-xs tw-font-mono tw-text-dt-text-secondary tw-truncate">
                  id: {{ group.kept.id }}
                </span>
                <span
                  class="tw-text-xs tw-font-mono tw-bg-color-status-success-bg tw-text-color-status-success-text tw-px-1.5 tw-py-0.5 tw-rounded"
                >
                  dc: {{ formatDc(group.kept) }}
                </span>
              </div>
              @if (isBeforeExpanded($index)) {
                <pre
                  appGlossScroll
                  class="tw-m-0 tw-text-xs tw-font-mono tw-max-h-32 tw-overflow-auto tw-p-2 tw-bg-neutral-900 tw-text-dt-text-disabled tw-rounded tw-whitespace-pre-wrap tw-break-words tw-select-all gs-dark"
                  >{{ itemJson(group.kept) }}</pre
                >
              } @else {
                <div
                  class="tw-text-xs tw-font-mono tw-p-2 tw-bg-dt-surface-sunken tw-rounded tw-text-dt-text-secondary"
                >
                  {{ group.kept.id?.slice(0, 20) }}… ·
                  <span class="tw-font-semibold">newest copy kept</span>
                </div>
              }
            </div>
          }
        }
      </div>
    </div>
  </div>

  <!-- ── Account cascade: removed transactions + former account ── -->
  @if (hasAccountCascade && data.accountCleanup) {
    <hr class="tw-my-6 tw-border-dt-border-secondary" />
    <h3 class="tw-text-base tw-font-semibold tw-text-dt-text-headings tw-m-0 tw-mb-3">
      Removed transactions
    </h3>
    <p class="tw-text-xs tw-text-dt-text-tertiary tw-m-0 tw-mb-2">
      Each row: deleted transaction and the account it belonged to (by account id).
    </p>
    <div appGlossScroll class="tw-flex tw-flex-col tw-gap-3 tw-max-h-[45vh] tw-overflow-y-auto">
      @if (data.accountCleanup.deletedTransactions.length === 0) {
        <p class="tw-text-dt-text-tertiary tw-text-sm tw-m-0">No transactions removed.</p>
      }
      @for (row of data.accountCleanup.deletedTransactions; track $index) {
        <div
          class="tw-flex tw-gap-4 tw-w-full tw-border tw-border-dt-border-secondary tw-rounded-lg tw-p-3 tw-bg-dt-surface-elevated"
        >
          <div class="tw-flex-1 tw-min-w-0">
            <h4
              class="tw-text-xs tw-font-semibold tw-text-color-status-error-text tw-m-0 tw-mb-1 tw-uppercase"
            >
              Transaction
            </h4>
            <div class="tw-text-xs tw-font-mono tw-mb-1">id: {{ row.transaction.id }}</div>
            <button
              type="button"
              mat-stroked-button
              class="tw-text-xs"
              (click)="toggleAcctTxExpand($index)"
            >
              {{ isAcctTxExpanded($index) ? "Collapse" : "Expand" }}
            </button>
            @if (isAcctTxExpanded($index)) {
              <pre
                appGlossScroll
                class="tw-mt-2 tw-m-0 tw-text-xs tw-font-mono tw-max-h-40 tw-overflow-auto tw-p-2 tw-bg-neutral-900 tw-text-dt-text-disabled tw-rounded tw-whitespace-pre-wrap tw-break-words tw-select-all gs-dark"
                >{{ itemJson(row.transaction) }}</pre
              >
            }
          </div>
          <div class="tw-flex-1 tw-min-w-0">
            <h4
              class="tw-text-xs tw-font-semibold tw-text-dt-text-secondary tw-m-0 tw-mb-1 tw-uppercase"
            >
              Former account
            </h4>
            <div class="tw-text-xs tw-font-mono tw-mb-1">id: {{ row.formerAccount.id }}</div>
            @if (isAcctTxExpanded($index)) {
              <pre
                appGlossScroll
                class="tw-m-0 tw-text-xs tw-font-mono tw-max-h-40 tw-overflow-auto tw-p-2 tw-bg-dt-surface-sunken tw-rounded tw-whitespace-pre-wrap tw-break-words tw-select-all"
                >{{ itemJson(row.formerAccount) }}</pre
              >
            }
          </div>
        </div>
      }
    </div>

    <hr class="tw-my-6 tw-border-dt-border-secondary" />
    <h3 class="tw-text-base tw-font-semibold tw-text-dt-text-headings tw-m-0 tw-mb-3">
      Removed source transactions
    </h3>
    <p class="tw-text-xs tw-text-dt-text-tertiary tw-m-0 tw-mb-2">
      Each row: deleted source transaction and a transaction that referenced it (srId).
    </p>
    <div appGlossScroll class="tw-flex tw-flex-col tw-gap-3 tw-max-h-[45vh] tw-overflow-y-auto">
      @if (data.accountCleanup.deletedSources.length === 0) {
        <p class="tw-text-dt-text-tertiary tw-text-sm tw-m-0">No source transactions removed.</p>
      }
      @for (row of data.accountCleanup.deletedSources; track $index) {
        <div
          class="tw-flex tw-gap-4 tw-w-full tw-border tw-border-dt-border-secondary tw-rounded-lg tw-p-3 tw-bg-dt-surface-elevated"
        >
          <div class="tw-flex-1 tw-min-w-0">
            <h4
              class="tw-text-xs tw-font-semibold tw-text-color-status-error-text tw-m-0 tw-mb-1 tw-uppercase"
            >
              Source transaction
            </h4>
            <div class="tw-text-xs tw-font-mono tw-mb-1">id: {{ row.source.id }}</div>
            <button
              type="button"
              mat-stroked-button
              class="tw-text-xs"
              (click)="toggleAcctSrcExpand($index)"
            >
              {{ isAcctSrcExpanded($index) ? "Collapse" : "Expand" }}
            </button>
            @if (isAcctSrcExpanded($index)) {
              <pre
                appGlossScroll
                class="tw-mt-2 tw-m-0 tw-text-xs tw-font-mono tw-max-h-40 tw-overflow-auto tw-p-2 tw-bg-neutral-900 tw-text-dt-text-disabled tw-rounded tw-whitespace-pre-wrap tw-break-words tw-select-all gs-dark"
                >{{ itemJson(row.source) }}</pre
              >
            }
          </div>
          <div class="tw-flex-1 tw-min-w-0">
            <h4
              class="tw-text-xs tw-font-semibold tw-text-dt-text-action tw-m-0 tw-mb-1 tw-uppercase"
            >
              Referenced by transaction
            </h4>
            <div class="tw-text-xs tw-font-mono tw-mb-1">id: {{ row.formerTransaction.id }}</div>
            @if (isAcctSrcExpanded($index)) {
              <pre
                appGlossScroll
                class="tw-m-0 tw-text-xs tw-font-mono tw-max-h-40 tw-overflow-auto tw-p-2 tw-bg-dt-surface-sunken tw-rounded tw-whitespace-pre-wrap tw-break-words tw-select-all"
                >{{ itemJson(row.formerTransaction) }}</pre
              >
            }
          </div>
        </div>
      }
    </div>
  }

  <!-- ── Source section ── -->
  @if (hasSourceSection) {
    <hr class="tw-my-6 tw-border-dt-border-secondary" />
    <h3 class="tw-text-sm tw-font-semibold tw-text-dt-text-secondary tw-m-0 tw-mb-3">
      {{ sourceSectionTitle }}
    </h3>

    @if (isKeptOnlyLayout) {
      <!-- Kept-only layout: source on the left, corresponding transaction on the right -->
      <div class="tw-flex tw-flex-col tw-gap-3">
        @for (entry of data.keptSourceTransactions ?? []; track $index) {
          <div class="tw-flex tw-gap-4 tw-w-full">
            <!-- Left: Kept source transaction -->
            <div class="tw-flex-1 tw-min-w-0">
              <h4
                class="tw-text-xs tw-font-semibold tw-text-color-status-success-text tw-m-0 tw-mb-1 tw-uppercase tw-tracking-wide"
              >
                Source Transaction (Kept)
              </h4>
              <div
                class="tw-border-2 tw-border-color-status-success-border tw-rounded tw-bg-color-status-success-bg tw-p-2"
              >
                <div class="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mb-1">
                  <span class="tw-text-xs tw-font-mono tw-text-dt-text-secondary tw-truncate">
                    id: {{ entry.source.id }}
                  </span>
                  <button
                    type="button"
                    mat-stroked-button
                    class="tw-text-xs tw-shrink-0"
                    (click)="toggleSourceExpand($index)"
                  >
                    {{ isSourceExpanded($index) ? "Collapse" : "Expand" }}
                  </button>
                </div>
                @if (isSourceExpanded($index)) {
                  <pre
                    appGlossScroll
                    class="tw-m-0 tw-text-xs tw-font-mono tw-max-h-32 tw-overflow-auto tw-p-2 tw-bg-neutral-900 tw-text-dt-text-disabled tw-rounded tw-whitespace-pre-wrap tw-break-words tw-select-all gs-dark"
                    >{{ itemJson(entry.source) }}</pre
                  >
                }
              </div>
            </div>

            <!-- Right: Corresponding transaction -->
            <div class="tw-flex-1 tw-min-w-0">
              <h4
                class="tw-text-xs tw-font-semibold tw-text-dt-text-action tw-m-0 tw-mb-1 tw-uppercase tw-tracking-wide"
              >
                Corresponding Transaction
              </h4>
              @if (entry.linkedTransactions.length > 0) {
                <div class="tw-border tw-border-teal-200 tw-rounded tw-bg-teal-50 tw-p-2">
                  <div class="tw-flex tw-items-center tw-gap-2 tw-mb-1">
                    <span class="tw-text-xs tw-font-mono tw-text-dt-text-secondary tw-truncate">
                      id: {{ entry.linkedTransactions[0].id }}
                    </span>
                  </div>
                  @if (isSourceExpanded($index)) {
                    <pre
                      appGlossScroll
                      class="tw-m-0 tw-text-xs tw-font-mono tw-max-h-32 tw-overflow-auto tw-p-2 tw-bg-neutral-900 tw-text-dt-text-disabled tw-rounded tw-whitespace-pre-wrap tw-break-words tw-select-all gs-dark"
                      >{{ itemJson(entry.linkedTransactions[0]) }}</pre
                    >
                  }
                </div>
              } @else {
                <div
                  class="tw-border tw-border-dt-border-secondary tw-rounded tw-bg-dt-surface-elevated tw-p-2"
                >
                  <span class="tw-text-xs tw-text-dt-text-tertiary tw-italic"
                    >No linked transaction</span
                  >
                </div>
              }
            </div>
          </div>
        }
      </div>
    } @else {
      <!-- Full layout: deleted on the left, kept on the right -->
      <div class="tw-flex tw-gap-4 tw-w-full">
        <!-- Left: Deleted source transactions -->
        <div class="tw-flex-1 tw-min-w-0">
          <h4
            class="tw-text-xs tw-font-semibold tw-text-color-status-error-text tw-m-0 tw-mb-2 tw-uppercase tw-tracking-wide"
          >
            Deleted ({{ data.deletedSourceTransactions?.length ?? 0 }})
          </h4>
          <div
            appGlossScroll
            class="tw-flex tw-flex-col tw-gap-2 tw-border tw-border-dt-border-secondary tw-rounded-lg tw-p-3 tw-bg-dt-surface-elevated tw-max-h-[40vh] tw-overflow-y-auto"
          >
            @if ((data.deletedSourceTransactions?.length ?? 0) === 0) {
              <p class="tw-text-dt-text-tertiary tw-text-xs tw-m-0">
                No source transactions deleted.
              </p>
            }
            @for (src of data.deletedSourceTransactions ?? []; track $index) {
              <div
                class="tw-border tw-border-color-status-error-border tw-rounded tw-bg-color-status-error-bg tw-p-2"
              >
                <div class="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mb-1">
                  <span class="tw-text-xs tw-font-mono tw-text-dt-text-secondary tw-truncate">
                    id: {{ src.id }}
                  </span>
                  <button
                    type="button"
                    mat-stroked-button
                    class="tw-text-xs tw-shrink-0"
                    (click)="toggleSourceExpand($index)"
                  >
                    {{ isSourceExpanded($index) ? "Collapse" : "Expand" }}
                  </button>
                </div>
                @if (isSourceExpanded($index)) {
                  <pre
                    appGlossScroll
                    class="tw-m-0 tw-text-xs tw-font-mono tw-max-h-32 tw-overflow-auto tw-p-2 tw-bg-neutral-900 tw-text-dt-text-disabled tw-rounded tw-whitespace-pre-wrap tw-break-words tw-select-all gs-dark"
                    >{{ itemJson(src) }}</pre
                  >
                }
              </div>
            }
          </div>
        </div>

        <!-- Right: Kept source transactions with their linked transactions -->
        <div class="tw-flex-1 tw-min-w-0">
          <h4
            class="tw-text-xs tw-font-semibold tw-text-color-status-success-text tw-m-0 tw-mb-2 tw-uppercase tw-tracking-wide"
          >
            Not Deleted ({{ data.keptSourceTransactions?.length ?? 0 }})
          </h4>
          <div
            appGlossScroll
            class="tw-flex tw-flex-col tw-gap-2 tw-border tw-border-dt-border-secondary tw-rounded-lg tw-p-3 tw-bg-dt-surface-elevated tw-max-h-[40vh] tw-overflow-y-auto"
          >
            @if ((data.keptSourceTransactions?.length ?? 0) === 0) {
              <p class="tw-text-dt-text-tertiary tw-text-xs tw-m-0">No source transactions kept.</p>
            }
            @for (entry of data.keptSourceTransactions ?? []; track $index) {
              <div
                class="tw-border-2 tw-border-color-status-success-border tw-rounded tw-bg-color-status-success-bg tw-p-2"
              >
                <div class="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mb-1">
                  <span class="tw-text-xs tw-font-mono tw-text-dt-text-secondary tw-truncate">
                    source id: {{ entry.source.id }}
                  </span>
                  <button
                    type="button"
                    mat-stroked-button
                    class="tw-text-xs tw-shrink-0"
                    (click)="toggleSourceExpand(10000 + $index)"
                  >
                    {{ isSourceExpanded(10000 + $index) ? "Collapse" : "Expand" }}
                  </button>
                </div>
                @if (entry.linkedTransactions.length > 0) {
                  <div
                    class="tw-mt-1 tw-text-xs tw-text-color-status-success-text tw-font-semibold"
                  >
                    Linked transaction(s): {{ entry.linkedTransactions.length }}
                  </div>
                }
                @if (isSourceExpanded(10000 + $index)) {
                  <pre
                    appGlossScroll
                    class="tw-m-0 tw-mt-1 tw-text-xs tw-font-mono tw-max-h-24 tw-overflow-auto tw-p-2 tw-bg-neutral-900 tw-text-dt-text-disabled tw-rounded tw-whitespace-pre-wrap tw-break-words tw-select-all gs-dark"
                    >{{ itemJson(entry.source) }}</pre
                  >
                  @if (entry.linkedTransactions.length > 0) {
                    <div
                      class="tw-mt-2 tw-pl-3 tw-border-0 tw-border-l-2 tw-border-color-status-success-border"
                    >
                      @for (tx of entry.linkedTransactions; track $index) {
                        <div class="tw-mt-1">
                          <span class="tw-text-xs tw-font-mono tw-text-dt-text-secondary">
                            tx id: {{ tx.id }}
                          </span>
                          <pre
                            appGlossScroll
                            class="tw-m-0 tw-mt-1 tw-text-xs tw-font-mono tw-max-h-24 tw-overflow-auto tw-p-2 tw-bg-dt-surface-sunken tw-rounded tw-whitespace-pre-wrap tw-break-words tw-select-all"
                            >{{ itemJson(tx) }}</pre
                          >
                        </div>
                      }
                    </div>
                  }
                }
              </div>
            }
          </div>
        </div>
      </div>
    }
  }
</div>
<div mat-dialog-actions class="tw-flex tw-justify-end tw-gap-2">
  <button mat-button mat-dialog-close type="button">Close</button>
</div>
